
import React, { useState } from 'react';
import { useApp } from '../AppContext';
import { ArrowLeft, Send, CheckCircle, Tag, Loader2, XCircle, Package, MapPin, ShoppingCart } from 'lucide-react';
import { couponService } from '../lib/storeService';
import { Coupon } from '../types';

const CheckoutPage: React.FC = () => {
  const { cart, currentStore, clearCart, isLoading } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    zipCode: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    reference: '',
    observation: '',
    coupon: ''
  });
  
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isOrdered, setIsOrdered] = useState(false);

  const subtotal = cart.reduce((acc, item) => acc + (item.product.price * item.quantity), 0);
  const deliveryFee = 5.00;
  
  let discount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.type === 'percent') {
      discount = subtotal * (appliedCoupon.value / 100);
    } else {
      discount = appliedCoupon.value;
    }
  }

  const total = Math.max(0, subtotal + deliveryFee - discount);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'coupon') {
      setCouponError(null);
      if (appliedCoupon && value.trim() === '') {
        setAppliedCoupon(null);
      }
    }
  };

  const validateCoupon = async () => {
    const code = formData.coupon.trim().toUpperCase();
    if (!code || !currentStore) return;
    
    setIsValidatingCoupon(true);
    setCouponError(null);
    
    try {
      const { data, error } = await couponService.validateCoupon(currentStore.id, code);
      if (error || !data) {
        setCouponError("Cupom inválido ou expirado.");
        setAppliedCoupon(null);
      } else {
        setAppliedCoupon(data);
        setCouponError(null);
      }
    } catch (err) {
      setCouponError("Erro ao validar cupom.");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (cart.length === 0 || !currentStore) return;
    
    const itemsText = cart.map(item => `- ${item.quantity}x ${item.product.name} (R$ ${(item.product.price * item.quantity).toFixed(2)})`).join('%0A');
    
    let summary = `*Pedido em ${currentStore.name}*%0A%0A`;
    summary += `*Cliente:* ${formData.name}%0A`;
    summary += `*Telefone:* ${formData.phone}%0A%0A`;
    
    summary += `*Endereço de Entrega:*%0A`;
    summary += `${formData.street}, nº ${formData.number}%0A`;
    summary += `${formData.neighborhood} - ${formData.city}%0A`;
    summary += `CEP: ${formData.zipCode}%0A`;
    if (formData.reference) summary += `Ref: ${formData.reference}%0A`;
    summary += `%0A`;

    summary += `*Produtos:*%0A${itemsText}%0A%0A`;
    summary += `*Subtotal:* R$ ${subtotal.toFixed(2)}%0A`;
    if (appliedCoupon) {
      summary += `*Cupom Aplicado:* ${appliedCoupon.code} (-R$ ${discount.toFixed(2)})%0A`;
    }
    summary += `*Entrega:* R$ ${deliveryFee.toFixed(2)}%0A`;
    summary += `*Total Final:* R$ ${total.toFixed(2)}%0A%0A`;
    summary += `*Obs:* ${formData.observation || 'Sem observações'}`;
    
    const waUrl = `https://wa.me/${currentStore.whatsapp_number.replace(/\D/g, '')}?text=${summary}`;
    window.open(waUrl, '_blank');
    
    setIsOrdered(true);
    clearCart();
  };

  const handleGoBack = () => {
    if (currentStore) {
      window.location.hash = `#/${currentStore.slug}`;
    } else {
      window.location.hash = '#/';
    }
  };

  const isFormValid = 
    formData.name.trim() !== '' && 
    formData.phone.trim() !== '' && 
    formData.zipCode.trim() !== '' && 
    formData.street.trim() !== '' && 
    formData.number.trim() !== '' && 
    formData.neighborhood.trim() !== '' && 
    formData.city.trim() !== '';

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="animate-spin text-indigo-600" size={48} />
        <p className="text-gray-500 font-medium animate-pulse">Sincronizando dados...</p>
      </div>
    );
  }

  if (isOrdered) {
    return (
      <div className="max-w-lg mx-auto p-8 mt-12 text-center bg-white rounded-[3rem] shadow-2xl border border-gray-100 animate-in zoom-in">
        <div className="inline-flex items-center justify-center p-6 bg-green-50 text-green-500 rounded-full mb-6">
          <CheckCircle size={72} />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Pedido Realizado!</h2>
        <p className="text-gray-500 font-medium mb-8 leading-relaxed">
          Quase lá! Agora finalize o atendimento no WhatsApp da loja para confirmar o pagamento e a entrega.
        </p>
        <button onClick={handleGoBack} className="w-full bg-indigo-600 text-white font-black py-5 rounded-2xl shadow-xl hover:bg-indigo-700 transition-all active:scale-95">Voltar ao Catálogo</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 pb-32 font-sans">
      <button onClick={handleGoBack} className="flex items-center gap-2 text-gray-400 hover:text-indigo-600 font-bold text-sm mb-10 transition-all hover:translate-x-[-4px]">
        <ArrowLeft size={18} />VOLTAR PARA O CATÁLOGO
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3 space-y-12">
          <div>
            <h2 className="text-4xl font-black text-gray-900 tracking-tight mb-2">Finalizar Pedido</h2>
            <p className="text-gray-400 font-medium">Preencha os dados abaixo para receber seu pedido</p>
          </div>

          <div className="space-y-10">
            <section className="space-y-6">
              <h4 className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em] flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px]">1</span>Identificação
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Nome Completo *</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleInputChange} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 bg-white outline-none font-bold focus:border-indigo-600 transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">WhatsApp *</label>
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleInputChange} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 bg-white outline-none font-bold focus:border-indigo-600 transition-all" />
                </div>
              </div>
            </section>

            <section className="space-y-6">
              <h4 className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em] flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px]">2</span>Endereço de Entrega
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-1 space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">CEP *</label>
                  <input type="text" name="zipCode" required value={formData.zipCode} onChange={handleInputChange} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 bg-white outline-none font-bold focus:border-indigo-600 transition-all" />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Cidade *</label>
                  <input type="text" name="city" required value={formData.city} onChange={handleInputChange} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 bg-white outline-none font-bold focus:border-indigo-600 transition-all" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                <div className="md:col-span-3 space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Rua / Avenida *</label>
                  <input type="text" name="street" required value={formData.street} onChange={handleInputChange} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 bg-white outline-none font-bold focus:border-indigo-600 transition-all" />
                </div>
                <div className="md:col-span-1 space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Número *</label>
                  <input type="text" name="number" required value={formData.number} onChange={handleInputChange} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 bg-white outline-none font-bold focus:border-indigo-600 transition-all" />
                </div>
              </div>
              <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Bairro *</label>
                  <input type="text" name="neighborhood" required value={formData.neighborhood} onChange={handleInputChange} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 bg-white outline-none font-bold focus:border-indigo-600 transition-all" />
              </div>
            </section>

            <section className="space-y-6">
              <h4 className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em] flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px]">3</span>Extras
              </h4>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 flex justify-between">Cupom de Desconto<span className="text-indigo-400 lowercase">Opcional</span></label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <input type="text" name="coupon" value={formData.coupon} onChange={handleInputChange} className={`w-full pl-12 pr-6 py-4 rounded-2xl border-2 outline-none font-black uppercase transition-all ${appliedCoupon ? 'border-green-100 bg-green-50 text-green-700' : 'border-gray-100 bg-white focus:border-indigo-600'}`} placeholder="CÓDIGO" />
                    <Tag className={`absolute left-4 top-1/2 -translate-y-1/2 ${appliedCoupon ? 'text-green-500' : 'text-gray-300'}`} size={20} />
                  </div>
                  <button type="button" onClick={validateCoupon} disabled={!formData.coupon || isValidatingCoupon || !!appliedCoupon} className="bg-gray-900 text-white font-black px-6 rounded-2xl shadow-lg hover:bg-black disabled:opacity-30 min-w-[100px] transition-all">
                    {isValidatingCoupon ? <Loader2 className="animate-spin mx-auto" size={20} /> : 'APLICAR'}
                  </button>
                </div>
                {couponError && <p className="text-xs font-bold text-red-500 mt-2 ml-1 flex items-center gap-1 animate-in shake"><XCircle size={14} /> {couponError}</p>}
                {appliedCoupon && <p className="text-xs font-bold text-green-600 mt-2 ml-1 flex items-center gap-1 animate-in zoom-in"><CheckCircle size={14} /> Cupom de {appliedCoupon.type === 'percent' ? `${appliedCoupon.value}%` : `R$ ${appliedCoupon.value.toFixed(2)}`} aplicado!</p>}
              </div>
            </section>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="sticky top-24 space-y-6">
            <div className="bg-white rounded-[2.5rem] p-8 shadow-2xl shadow-indigo-100 border border-gray-100">
              <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2"><Package className="text-indigo-600" size={24} />Resumo</h3>
              <div className="space-y-4 max-h-[30vh] overflow-y-auto pr-2 mb-8 custom-scrollbar">
                {cart.map(item => (
                  <div key={item.product.id} className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-800 text-sm truncate">{item.quantity}x {item.product.name}</p>
                    </div>
                    <span className="font-black text-gray-900 text-sm">R$ {(item.product.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-3 pt-6 border-t border-gray-100">
                <div className="flex justify-between text-xs font-bold text-gray-400"><span>SUBTOTAL</span><span>R$ {subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-xs font-bold text-gray-400"><span>ENTREGA</span><span>R$ {deliveryFee.toFixed(2)}</span></div>
                {appliedCoupon && (
                  <div className="flex justify-between text-xs font-bold text-green-600 bg-green-50 p-3 rounded-xl border border-green-100">
                    <span>DESCONTO</span><span>- R$ {discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-end pt-5 border-t-2 border-dashed border-gray-100">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">TOTAL</span>
                  <span className="text-4xl font-black text-indigo-600 tracking-tighter">R$ {total.toFixed(2)}</span>
                </div>
              </div>
              <button onClick={handleSubmit} disabled={cart.length === 0 || !isFormValid} className="w-full mt-10 bg-indigo-600 hover:bg-indigo-700 text-white font-black py-5 rounded-2xl shadow-xl flex items-center justify-center gap-3 transition-all disabled:opacity-40"><Send size={24} />ENVIAR NO WHATSAPP</button>
            </div>
            <div className="p-6 bg-amber-50 rounded-[1.5rem] border border-amber-100 flex gap-4">
               <MapPin size={24} className="text-amber-500 flex-shrink-0 mt-1" />
               <p className="text-[11px] font-medium text-amber-700 leading-relaxed">Após clicar em enviar, uma conversa será aberta no seu WhatsApp com todos os detalhes do pedido.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
